import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import { getSellPaymentReport, getSellPaymentFilterOptions } from '../../services/reportApi';
import { formatDateTime } from '../../utils/dateUtils';

const SellPaymentReport = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [filterOptions, setFilterOptions] = useState({ locations: [], customers: [], customer_groups: [] });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', location_id: '', customer_id: '', customer_group_id: '' });
    const [showFilters, setShowFilters] = useState(false);

    const fetchData = useCallback(async (pg = 1, perPage = entriesPerPage) => {
        setLoading(true); setError(null);
        try {
            const res = await getSellPaymentReport({ ...filters, page: pg, per_page: perPage });
            if (res.success) {
                setData(res.data.data || []);
                setTotal(res.data.total || 0);
                setLastPage(res.data.last_page || 1);
                setPage(res.data.current_page || pg);
            }
        } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, [filters, entriesPerPage]);

    useEffect(() => {
        fetchData(1, entriesPerPage);
        getSellPaymentFilterOptions().then(r => { if (r.success) setFilterOptions(r.data); }).catch(() => {});
    }, []);
    useEffect(() => { fetchData(1, entriesPerPage); }, [entriesPerPage]);

    const handleFilterApply = () => { fetchData(1, entriesPerPage); setShowFilters(false); };
    const handleFilterReset = () => setFilters({ start_date: '', end_date: '', location_id: '', customer_id: '', customer_group_id: '' });

    const filteredData = data.filter(r =>
        (r.payment_ref_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.customer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.invoice_no || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const buildMethodLabel = (row) => {
        let m = row.method_label || row.method || '';
        if (row.method === 'cheque' && row.cheque_number) m += `\n(Cheque No.: ${row.cheque_number})`;
        else if (row.method === 'card' && row.card_transaction_number) m += `\n(Card No.: ${row.card_transaction_number})`;
        else if (row.method === 'bank_transfer' && row.bank_account_number) m += `\n(Bank A/C: ${row.bank_account_number})`;
        return m;
    };

    const handleExport = (format) => {
        const headers = ['Reference No', 'Paid on', 'Amount', 'Customer', 'Customer Group', 'Payment Method', 'Sell'];
        const rows = filteredData.map(r => [
            r.payment_ref_no || '', r.paid_on ? formatDateTime(r.paid_on) : '',
            r.amount_formatted || '', r.customer || '', r.customer_group || '',
            buildMethodLabel(r).replace(/\n/g, ' '), r.invoice_no || ''
        ]);
        const title = 'Sell Payment Report'; const filename = 'sell_payment_report';
        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
        setShowExportDropdown(false);
    };

    const from = total === 0 ? 0 : (page - 1) * entriesPerPage + 1;
    const to = Math.min(page * entriesPerPage, total);
    const inputStyle = { padding: '0.35rem 0.6rem', border: '1px solid #e2e8f0', borderRadius: '5px', fontSize: '0.8rem', outline: 'none', background: '#fff' };
    const selStyle = { ...inputStyle, cursor: 'pointer' };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Filters ── */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setShowFilters(v => !v)} style={{ ...inputStyle, cursor: 'pointer', background: showFilters ? '#3b82f6' : '#fff', color: showFilters ? '#fff' : '#334155', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <i className="fa fa-filter" /> Filters
                </button>
                {showFilters && (<>
                    <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                    <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />
                    <select value={filters.location_id} onChange={e => setFilters(f => ({ ...f, location_id: e.target.value }))} style={selStyle}>
                        <option value="">All Locations</option>
                        {filterOptions.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <select value={filters.customer_id} onChange={e => setFilters(f => ({ ...f, customer_id: e.target.value }))} style={selStyle}>
                        <option value="">All Customers</option>
                        {filterOptions.customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={filters.customer_group_id} onChange={e => setFilters(f => ({ ...f, customer_group_id: e.target.value }))} style={selStyle}>
                        <option value="">All Customer Groups</option>
                        {filterOptions.customer_groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                    <button onClick={handleFilterApply} style={{ ...inputStyle, background: '#3b82f6', color: '#fff', cursor: 'pointer', border: 'none' }}>Apply</button>
                    <button onClick={handleFilterReset} style={{ ...inputStyle, cursor: 'pointer' }}>Reset</button>
                </>)}
            </div>

            {/* ── Table Container ── */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid #e2e8f0', padding: '1rem 1.25rem' }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>
                    <div className="table-controls-right">
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                                    {[{ icon: 'fa-file-excel-o', label: 'Export CSV', action: () => handleExport('CSV') }, { icon: 'fa-file-excel-o', label: 'Export Excel', action: () => handleExport('Excel') }, { icon: 'fa-print', label: 'Print', action: () => handleExport('Print') }, { icon: 'fa-file-pdf-o', label: 'Export PDF', action: () => handleExport('PDF') }].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: '#475569' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <i className={`fa ${item.icon}`} style={{ width: '14px', textAlign: 'center' }} />{item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input type="text" placeholder="Search ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px' }} />
                    </div>
                </div>

                {error && <div style={{ padding: '1rem', color: '#ef4444', fontSize: '0.875rem' }}>Error: {error}</div>}

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Reference No', 'Paid on', 'Amount', 'Customer', 'Customer Group', 'Payment Method', 'Sell', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No data found</td></tr>
                            ) : filteredData.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>
                                        {row.is_return && <i className="fa fa-plus-circle" style={{ color: '#22c55e', fontSize: '1rem', marginRight: '0.35rem', cursor: 'pointer' }} />}
                                        {row.payment_ref_no}
                                    </td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569', whiteSpace: 'nowrap' }}>{row.paid_on ? formatDateTime(row.paid_on) : ''}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '500' }}>{row.amount_formatted}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.customer}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.customer_group}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569', whiteSpace: 'pre-line' }}>{buildMethodLabel(row)}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#3b82f6', cursor: 'pointer' }}>{row.invoice_no}</td>
                                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                        <button style={{ padding: '0.25rem 0.75rem', background: '#fff', border: '1px solid #a855f7', color: '#a855f7', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}>View</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
                        {total > 0 ? `Showing ${from} to ${to} of ${total} entries` : 'No entries'}
                    </div>
                    <div className="pagination-controls">
                        <button className={`page-btn${page <= 1 ? ' disabled' : ''}`} onClick={() => { if (page > 1) fetchData(page - 1); }} disabled={page <= 1}>Previous</button>
                        {Array.from({ length: Math.min(lastPage, 5) }, (_, i) => { const pg = page <= 3 ? i + 1 : page - 2 + i; if (pg < 1 || pg > lastPage) return null; return <button key={pg} className={`page-btn${pg === page ? ' active' : ''}`} onClick={() => fetchData(pg)}>{pg}</button>; })}
                        <button className={`page-btn${page >= lastPage ? ' disabled' : ''}`} onClick={() => { if (page < lastPage) fetchData(page + 1); }} disabled={page >= lastPage}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellPaymentReport;
